import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { getChatHistory, getChatsList, promptChat } from '../../api'
import { ChatHistoryTypeAPI, ChatsListTypeAPI } from '../../api/types'
import chatbotIcon from '../../assets/icons/chatbot.svg'
import { Loading } from '../../components/shared/Loading'
import { Scroll } from '../../elements/Scroll'
import { Sidebar } from '../../elements/Sidebar'
import { TextInput } from '../../elements/TextInput'
import { getPaths } from '../../routing/routes'

import styles from './ChatbotPage.module.scss'


type ChatbotPageProps = {
  chatId?: number
}


export const ChatbotPage = ({ chatId }: ChatbotPageProps) => {
  const i18n = useTranslation()
  const navigate = useNavigate()
  const [loadingChatsList, setLoadingChatsList] = useState(true)
  const [loadingMessageHistory, setLoadingMessageHistory] = useState(chatId ? true : false)
  const [loadingBotResponse, setLoadingBotResponse] = useState(false)
  const [chatsList, setChatsList] = useState<ChatsListTypeAPI['chats']>([])
  const [messages, setMessages] = useState<ChatHistoryTypeAPI['messages']>([])
  const [prompt, setPrompt] = useState<string>('')


  useEffect(() => {
    getChatsList().then(response => {
      if (!response.success) {
        setLoadingChatsList(false)
        toast.error(`${i18n.t('ToastError:ChatbotListError')}: ${response.error}`)
        return
      }
      setChatsList(response.data.chats)
      setLoadingChatsList(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  useEffect(() => {
    if (!chatId) return

    setLoadingMessageHistory(true)
    getChatHistory(chatId).then(response => {
      if (!response.success) {
        setLoadingMessageHistory(false)
        toast.error(`${i18n.t('ToastError:ChatMessageHistoryError')}: ${response.error}`)
        return
      }
      setMessages(response.data.messages)
      setLoadingMessageHistory(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId])


  const promptChatbot = useCallback(() => {
    if (!prompt) return
    if (!chatId) return

    setLoadingBotResponse(true)
    setMessages(prev => [
      ...prev,
      {
        text: prompt,
        writtenAt: new Date().toISOString(),
        owner: 'person'
      }
    ])
    promptChat(chatId, { prompt }).then(response => {
      if (!response.success) {
        setLoadingBotResponse(false)
        toast.error(`${i18n.t('ToastError:ChatMessageHistoryError')}: ${response.error}`)
        return
      }
      setMessages(prev => [...prev, response.data.message])
      setPrompt('')
      setLoadingBotResponse(false)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, prompt])


  return (
    <div className={styles.chatbotPageContainer}>
      <h1>
        {i18n.t('Common:Chatbot')}
      </h1>
      <div className={styles.content}>
        <Sidebar
          cookieName='chatbotSidebarState'
          isActiveChecker={id => id === chatId}
          itemsTop={
            chatsList.map(chat => ({
              id: chat.id,
              icon: chatbotIcon,
              title: chat.title,
              handler: () => navigate(getPaths.brands.chatbot.chat(chat.id))
            }))
          }
          beforeBottomItems={loadingChatsList && <Loading />}
        />
        {loadingMessageHistory ? (
          <Loading />
        ) : (
          <div className={styles.mainContent}>
            <Scroll<ChatHistoryTypeAPI['messages'][0]>
              options={messages}
              height={'100%'}
              variant='transparent'
              getOptionKey={message => message.writtenAt}
              bottomoSpace='7rem'
              getOptionComponent={message => (
                <div
                  key={`chat_${chatId}_message_at_${message.writtenAt}`}
                  className={`${styles.message} ${message.owner === 'person' ? styles.user : styles.bot}`}
                >
                  {message.text.split('\n').map((line, index) => (
                    <p key={`chat_${chatId}_message_at_${message.writtenAt}_line_${index}`}>
                      {line}
                    </p>
                  ))}
                </div>
              )}
            />
            <div className={styles.promptContainer}>
              <TextInput
                placeholder={i18n.t('Chatbot:AskAnything')}
                value={prompt}
                onChange={setPrompt}
                onEnter={() => promptChatbot()}
                multiline
                disabled={loadingMessageHistory || loadingBotResponse}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
